import "react-phone-number-input/style.css";

import { Api, AuthController, loadAuthService } from "@somewear/auth";
import { someGrpc } from "@somewear/grpc";
import { Configuration, Sentry } from "@somewear/model";
import {
	commonCss,
	componentsCss,
	conversationCss,
	indexCss,
	mapCss,
	sidebarCss,
} from "@somewear/ui";
import type { RootState } from "@web/app/rootReducer";
import store from "@web/app/store";
import Loader from "@web/common/Loader";
import { UnsavedChangesProvider } from "@web/common/navigation/unsavedChanges";
import GlobalErrorComponent from "@web/components/GlobalErrorComponent";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Outlet } from "react-router-dom";

import Config from "../config/Config";

indexCss();
conversationCss();
componentsCss();
sidebarCss();
commonCss();
mapCss();

let initPromise: Promise<void> | undefined;

/** One-time app bootstrap; guarded so a remount never re-initializes. */
function initApp(): Promise<void> {
	if (initPromise === undefined) {
		Configuration.init(Config);
		Sentry.init({
			dsn: Config.sentry.dsn,
			enabled: Config.sentry.enable,
			environment: Config.sentry.environment,
			integrations: [new Sentry.Integrations.Breadcrumbs({ console: false, dom: false })],
		});
		someGrpc.init(Config);
		Api.init(store);
		initPromise = loadAuthService(Config).then((service) => {
			AuthController.init(service);
		});
	}
	return initPromise;
}

/**
 * Root route element: bootstraps config/Sentry/grpc/Api, gates rendering on
 * the auth service loading, and renders the global error screen when set.
 */
const Root: React.FunctionComponent = () => {
	const error = useSelector((state: RootState) => state.app.globalError);
	const [initialized, setInitialized] = useState(false);

	useEffect(() => {
		let active = true;
		initApp().then(() => {
			if (active) setInitialized(true);
		});
		return () => {
			active = false;
		};
	}, []);

	if (error) {
		return <GlobalErrorComponent />;
	}
	if (!initialized) {
		return <Loader fullScreen={true} />;
	}
	return (
		<UnsavedChangesProvider>
			<Outlet />
		</UnsavedChangesProvider>
	);
};

export default Root;
